import { useState } from 'react'
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card } from '@/components/primitives'
import { toast } from 'sonner'
import { EnvelopeSimple, Lock, Eye, EyeSlash } from '@phosphor-icons/react'
import { PinDayLogo } from '@/components/ui/PinDayLogo'
import { resolveRouteLocale } from '@/lib/route-locale'
import { TwoFactorLoginStep } from './TwoFactorLoginStep'

type Phase =
  | { kind: 'credentials' }
  | { kind: 'challenge'; challengeToken: string }
  | { kind: 'enroll'; setupToken: string; orgName?: string }

export function LoginPage() {
  const { t } = useTranslation('auth')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [phase, setPhase] = useState<Phase>({ kind: 'credentials' })
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { locale: routeLocale } = useParams<{ locale: string }>()
  const locale = resolveRouteLocale([routeLocale])
  const locationState = location.state as {
    from?: { pathname?: string; search?: string; hash?: string }
  } | null

  const fromLocation = locationState?.from
  const from = fromLocation?.pathname
    ? `${fromLocation.pathname}${fromLocation.search ?? ''}${fromLocation.hash ?? ''}`
    : `/${locale}/dashboard`

  const goToDashboard = () => {
    toast.success(t('login.title') + '!')
    navigate(from, { replace: true })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email || !password) {
      toast.error(t('errors.required'))
      return
    }

    setIsLoading(true)

    try {
      const result = await login(email, password)
      if (result.status === 'authenticated') {
        goToDashboard()
      } else if (result.status === 'two_factor_required') {
        setPhase({ kind: 'challenge', challengeToken: result.challengeToken })
      } else if (result.status === 'two_factor_setup_required') {
        setPhase({ kind: 'enroll', setupToken: result.setupToken, orgName: result.tenant?.name })
      }
    } catch {
      toast.error(t('errors.invalidCredentials'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[var(--pd-bg)] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to={`/${locale}/`} className="inline-flex justify-center">
            <PinDayLogo size="xl" />
          </Link>
          <p className="text-[var(--pd-foreground)]/72 mt-2 font-medium">
            {t('login.subtitle')}
          </p>
        </div>

        {/* Card de Login */}
        <Card className="border-[var(--pd-border)]/90 shadow-[var(--pd-shadow-lg)]">
          <div className="p-8">
            {phase.kind !== 'credentials' ? (
              <TwoFactorLoginStep
                phase={phase}
                onAuthenticated={goToDashboard}
                onBack={() => setPhase({ kind: 'credentials' })}
              />
            ) : (
            <>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[var(--pd-foreground)]/90">
                  {t('login.email')}
                </Label>
                <div className="relative">
                  <EnvelopeSimple 
                    size={20} 
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pd-foreground)]/74"
                  />
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('login.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="auth-input pl-10 h-11 !bg-[var(--pd-input-bg)] !text-[var(--pd-foreground)] !border-[var(--pd-border)] placeholder:!text-[var(--pd-foreground)]/72 focus-visible:!border-[var(--pd-accent)] focus-visible:!ring-[var(--pd-ring)]/70"
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[var(--pd-foreground)]/90">
                  {t('login.password')}
                </Label>
                <div className="relative">
                  <Lock 
                    size={20} 
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pd-foreground)]/74"
                  />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('login.passwordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="auth-input pl-10 pr-10 h-11 !bg-[var(--pd-input-bg)] !text-[var(--pd-foreground)] !border-[var(--pd-border)] placeholder:!text-[var(--pd-foreground)]/72 focus-visible:!border-[var(--pd-accent)] focus-visible:!ring-[var(--pd-ring)]/70"
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--pd-foreground)]/74 hover:text-[var(--pd-foreground)] transition-colors"
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Esqueci senha */}
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="remember"
                    className="rounded border-[var(--pd-border)] bg-[var(--pd-muted)] text-[var(--pd-primary)]"
                  />
                  <label htmlFor="remember" className="ml-2 text-[var(--pd-foreground)]/72">
                    {t('login.rememberMe')}
                  </label>
                </div>
                <Link
                  to={`/${locale}/forgot-password`}
                  className="text-[var(--pd-foreground)]/92 hover:text-[var(--pd-accent)] transition-colors"
                >
                  {t('login.forgotPassword')}
                </Link>
              </div>

              {/* Botão de Login */}
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[var(--pd-accent)] hover:bg-[var(--pd-accent)]/92 text-[var(--pd-accent-foreground)] py-6 rounded-xl font-semibold text-base shadow-lg transition-all duration-200 disabled:bg-[var(--pd-card)] disabled:text-[var(--pd-foreground)]/65 disabled:border disabled:border-[var(--pd-border)] [&:disabled]:opacity-100"
              >
                {isLoading ? t('login.submitting') : t('login.submit')}
              </Button>
            </form>

            {/* Divisor */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[var(--pd-border)]"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-[var(--pd-card)] text-[var(--pd-foreground)]/72">
                  {t('login.or')}
                </span>
              </div>
            </div>

            {/* Criar conta */}
            <div className="text-center text-sm text-[var(--pd-foreground)]/72">
              {t('login.noAccount')}{' '}
              <Link
                to={`/${locale}/register`}
                className="text-[var(--pd-primary)] hover:text-[var(--pd-primary)]/80 font-semibold transition-colors"
              >
                {t('login.signUp')}
              </Link>
            </div>
            </>
            )}
          </div>
        </Card>

        {/* Link para voltar */}
        <div className="text-center mt-6">
          <Link
            to={`/${locale}/`}
            className="text-sm text-[var(--pd-foreground)]/72 hover:text-[var(--pd-foreground)] transition-colors"
          >
            ← {t('common:actions.back')}
          </Link>
        </div>
      </div>
    </div>
  )
}
